<!-- Inventory Report -->
<div class="inventory-report-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-main">
        <div class="header-icon">
          <i class="fas fa-boxes-stacked"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Inventory Report</h1>
          <p class="page-description">Track inventory performance, stock levels, and expiration data</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Tab Navigation -->
  <div class="data-panel">
    <div class="panel-header" style="padding: 0; border-bottom: none;">
      <ul ngbNav #nav="ngbNav" [(activeId)]="active" class="nav-tabs nav-fill" style="border-bottom: none; padding: 8px 20px 0; width: 100%;">
        <li [ngbNavItem]="1">
          <button ngbNavLink>
            <i class="fas fa-user-tie me-2"></i>Waiter Sales Report
          </button>
          <ng-template ngbNavContent>
            <!-- Waiter Sales Filters -->
            <div class="filters-panel" style="border-radius: 0; border-left: 0; border-right: 0; box-shadow: none; margin-bottom: 0;">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div class="search-box" style="flex: 1; max-width: 320px;">
                  <div class="search-input-wrapper">
                    <i class="fas fa-search search-icon"></i>
                    <input type="text" class="search-input" placeholder="Search Waiter" />
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <div ngbDropdown class="d-inline-block">
                    <button type="button" class="btn btn-filter" ngbDropdownToggle>
                      <i class="fas fa-calendar me-2"></i>
                      {{ selectedDateFilter === 'custom' ? 'Custom Date' : selectedDateFilter | titlecase }}
                      <i class="fas fa-chevron-down ms-2"></i>
                    </button>
                    <div ngbDropdownMenu class="p-1">
                      <button ngbDropdownItem (click)="onDateFilterChange('today')">Today</button>
                      <button ngbDropdownItem (click)="onDateFilterChange('yesterday')">Yesterday</button>
                      <button ngbDropdownItem (click)="onDateFilterChange('custom')">Custom Date</button>
                    </div>
                  </div>

                  <!-- Custom Date Range Picker -->
                  @if (showDateRangePicker) {
                    <div class="card position-absolute p-3" style="z-index: 1000; right: 20px; top: 100%;">
                      <div class="d-flex gap-2 align-items-center mb-2">
                        <div>
                          <label class="form-label">Start Date</label>
                          <div class="input-group">
                            <input
                              class="form-control"
                              placeholder="yyyy-mm-dd"
                              name="start"
                              [(ngModel)]="dateRange.start"
                              ngbDatepicker
                              #startDate="ngbDatepicker">
                            <button class="btn btn-outline-secondary" (click)="startDate.toggle()" type="button">
                              <i class="fa fa-solid fa-calendar"></i>
                            </button>
                          </div>
                        </div>
                        <div>
                          <label class="form-label">End Date</label>
                          <div class="input-group">
                            <input
                              class="form-control"
                              placeholder="yyyy-mm-dd"
                              name="end"
                              [(ngModel)]="dateRange.end"
                              ngbDatepicker
                              #endDate="ngbDatepicker">
                            <button class="btn btn-outline-secondary" (click)="endDate.toggle()" type="button">
                              <i class="fa fa-solid fa-calendar"></i>
                            </button>
                          </div>
                        </div>
                        <div class="d-flex align-items-end">
                          <button
                            class="btn btn-primary"
                            (click)="onDateRangeSelected()"
                            [disabled]="!dateRange.start || !dateRange.end">
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  }

                  <button class="btn btn-filter">
                    <i class="fas fa-file-export me-2"></i>Export Report
                  </button>
                </div>
              </div>
            </div>

            <!-- Waiter Sales Content -->
            <div style="padding: 20px;">
              @if (isLoading) {
                <div class="loading-state">
                  <div class="loading-content">
                    <div class="loading-spinner">
                      <div class="spinner-ring"></div>
                    </div>
                    <div class="loading-text">
                      <h5>Loading report...</h5>
                      <p>Fetching waiter sales data</p>
                    </div>
                  </div>
                </div>
              } @else if (error) {
                <div class="empty-state">
                  <div class="empty-icon">
                    <i class="fas fa-exclamation-circle fa-3x" style="color: var(--danger-text);"></i>
                  </div>
                  <h5 class="empty-title">Error Loading Data</h5>
                  <p class="empty-subtitle">{{ error }}</p>
                </div>
              } @else {
                @for (waiter of waiterSalesReport; track waiter.waiter_id) {
                  <div class="card mb-4" style="overflow: hidden;">
                    <div class="d-flex justify-content-between align-items-center" style="padding: 14px 20px; background: var(--bg-muted); border-bottom: 1px solid var(--border);">
                      <div class="d-flex align-items-center gap-2">
                        <div style="width: 32px; height: 32px; border-radius: var(--radius-sm); background: var(--accent-subtle); color: var(--accent); display: flex; align-items: center; justify-content: center; font-size: 13px;">
                          <i class="fas fa-user-tie"></i>
                        </div>
                        <span style="font-weight: 600; font-size: 14px; color: var(--text-primary);">{{ waiter.waiter_name }}</span>
                      </div>
                      <div class="d-flex gap-2">
                        <span class="badge badge-soft-info">Total Items: {{ waiter.total_quantity }}</span>
                        <span class="badge badge-soft-success">Total: {{ waiter.total_amount | currency:'&#8358; ' }}</span>
                      </div>
                    </div>
                    <div class="table-container">
                      <table class="table" style="margin-bottom: 0;">
                        <thead>
                          <tr>
                            <th class="ps-4">Item Name</th>
                            <th>Quantity Sold</th>
                            <th>Total Amount</th>
                            <th>Last Sale Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (sale of waiter.items; track sale.item_name) {
                            <tr>
                              <td class="ps-4">{{ sale.item_name }}</td>
                              <td>
                                <span class="badge badge-light">{{ sale.quantity_sold }}</span>
                              </td>
                              <td class="fw-semibold" style="color: var(--success-text);">{{ sale.total_amount | currency:'&#8358; ' }}</td>
                              <td style="color: var(--text-secondary);">{{ sale.last_sale_time }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                }

                @if (waiterSalesReport.length === 0) {
                  <div class="empty-state">
                    <div class="empty-icon">
                      <i class="fas fa-user-tie fa-3x"></i>
                    </div>
                    <h5 class="empty-title">No sales data found</h5>
                    <p class="empty-subtitle">No waiter sales records are available for the selected date range.</p>
                  </div>
                }
              }
            </div>
          </ng-template>
        </li>

        <li [ngbNavItem]="4">
          <button ngbNavLink>
            <i class="fas fa-clipboard-list me-2"></i>Inventory Summary Report
          </button>
          <ng-template ngbNavContent>
            <!-- Inventory Summary Filters -->
            <div class="filters-panel" style="border-radius: 0; border-left: 0; border-right: 0; box-shadow: none; margin-bottom: 0;">
              <div class="search-box" style="max-width: 320px;">
                <div class="search-input-wrapper">
                  <i class="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    class="search-input"
                    placeholder="Search Inventory"
                    [(ngModel)]="inventorySearch"
                    (ngModelChange)="onInventorySearch($event)" />
                </div>
              </div>
            </div>

            <!-- Inventory Summary Table -->
            <div class="table-container">
              <table class="table">
                <thead>
                  <tr>
                    <th class="ps-4">Item</th>
                    <th>Category</th>
                    <th>Current Stock</th>
                    <th>Qty Sold</th>
                    <th>Cost Price</th>
                    <th>Base Selling Price</th>
                    <th>Avg. Selling Price</th>
                    <th>Total Investment</th>
                    <th>Base Unrealized Profit</th>
                    <th>Avg. Unrealized Profit</th>
                  </tr>
                </thead>
                <tbody>
                  @if (filteredInventoryItems && filteredInventoryItems.length > 0) {
                    @for (item of filteredInventoryItems; track item.id) {
                      <tr>
                        <td class="ps-4 fw-medium">{{ item.name }}</td>
                        <td>
                          <span class="badge badge-light">{{ item.category }}</span>
                        </td>
                        <td>{{ item.current_stock }}</td>
                        <td>{{ item.quantity_sold }}</td>
                        <td>&#8358;{{ item.formatted.cost_price }}</td>
                        <td>&#8358;{{ item.formatted.base_selling_price }}</td>
                        <td>&#8358;{{ item.formatted.avg_price_tag_price }}</td>
                        <td class="fw-semibold">&#8358;{{ item.formatted.total_investment }}</td>
                        <td style="color: var(--success-text);">&#8358;{{ item.formatted.base_unrealized_profit }}</td>
                        <td style="color: var(--success-text);">&#8358;{{ item.formatted.avg_price_tag_unrealized_profit }}</td>
                      </tr>
                    }
                  } @else {
                    <tr>
                      <td colspan="10">
                        <div class="empty-state">
                          <div class="empty-icon">
                            <i class="fas fa-boxes-stacked fa-3x"></i>
                          </div>
                          <h5 class="empty-title">No data available</h5>
                          <p class="empty-subtitle">No inventory summary data to display.</p>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="panel-footer">
              <div>
                <select class="form-select" [(ngModel)]="pageSize" (change)="onPageSizeChange()" style="width: auto; min-width: 160px;">
                  <option [value]="10">10 items per page</option>
                  <option [value]="25">25 items per page</option>
                  <option [value]="50">50 items per page</option>
                </select>
              </div>
              <ngb-pagination
                [collectionSize]="totalItems"
                [(page)]="currentPage"
                [pageSize]="pageSize"
                (pageChange)="onPageChange($event)"
                [maxSize]="5"
                [rotate]="true"
                [boundaryLinks]="true">
              </ngb-pagination>
            </div>
          </ng-template>
        </li>

        <li [ngbNavItem]="2">
          <button ngbNavLink>
            <i class="fas fa-exclamation-triangle me-2"></i>Low Stock Alert Report
          </button>
          <ng-template ngbNavContent>
            <!-- Low Stock Filters -->
            <div class="filters-panel" style="border-radius: 0; border-left: 0; border-right: 0; box-shadow: none; margin-bottom: 0;">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div class="search-box" style="flex: 1; max-width: 320px;">
                  <div class="search-input-wrapper">
                    <i class="fas fa-search search-icon"></i>
                    <input
                      type="text"
                      class="search-input"
                      placeholder="Search"
                      [(ngModel)]="lowStockSearch"
                      (ngModelChange)="onLowStockSearch($event)" />
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <div ngbDropdown class="d-inline-block" container="body">
                    <button type="button" class="btn btn-filter" id="dropdownBasic1" ngbDropdownToggle>
                      <i class="fas fa-calendar me-2"></i>Filter by Date
                      <i class="fas fa-chevron-down ms-2"></i>
                    </button>
                    <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="p-1">
                      <button ngbDropdownItem>Today</button>
                      <button ngbDropdownItem>This week</button>
                      <button ngbDropdownItem>This Month</button>
                      <button ngbDropdownItem>Custom</button>
                    </div>
                  </div>
                  <div ngbDropdown class="d-inline-block" container="body">
                    <button type="button" class="btn btn-filter" id="dropdownBasic1" ngbDropdownToggle>
                      <i class="fas fa-filter me-2"></i>Filter by Category
                      <i class="fas fa-chevron-down ms-2"></i>
                    </button>
                    <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="p-1">
                      <button ngbDropdownItem>Today</button>
                      <button ngbDropdownItem>This week</button>
                      <button ngbDropdownItem>This Month</button>
                      <button ngbDropdownItem>Custom</button>
                    </div>
                  </div>
                  <button class="btn btn-filter">
                    <i class="fas fa-file-export me-2"></i>Export Report
                  </button>
                </div>
              </div>
            </div>

            <!-- Low Stock Table -->
            <div class="table-container">
              <table class="table">
                <thead>
                  <tr>
                    <th class="ps-4">Item</th>
                    <th>Category</th>
                    <th>Current Stock</th>
                    <th>Quantity Sold</th>
                    <th>Reorder Level</th>
                    <th>Restock Needed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of filteredLowStockItems; track item.item) {
                    <tr>
                      <td class="ps-4 fw-medium">{{ item.item }}</td>
                      <td>
                        <span class="badge badge-light">{{ item.category }}</span>
                      </td>
                      <td>
                        <span class="badge"
                              [class.badge-soft-danger]="item.status === 'Red'"
                              [class.badge-soft-warning]="item.status === 'Yellow'">
                          {{ item.current_stock }}
                          {{ item.status === 'Red' ? '(Critical)' : '(Low)' }}
                        </span>
                      </td>
                      <td>{{ item.quantity_sold }}</td>
                      <td>{{ item.reorder_level }}</td>
                      <td>
                        <span class="fw-semibold" style="color: var(--danger-text);">{{ item.restock_needed }}</span>
                      </td>
                      <td>
                        <div ngbDropdown container="body">
                          <button class="btn btn-sm btn-outline-secondary" ngbDropdownToggle>
                            Actions
                          </button>
                          <div ngbDropdownMenu>
                            <button ngbDropdownItem>
                              <i class="fas fa-truck me-2"></i>Restock
                            </button>
                            <button ngbDropdownItem>
                              <i class="fas fa-eye me-2"></i>Details
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  }
                  @if (filteredLowStockItems.length === 0) {
                    <tr>
                      <td colspan="7">
                        <div class="empty-state">
                          <div class="empty-icon">
                            <i class="fas fa-box-open fa-3x"></i>
                          </div>
                          <h5 class="empty-title">No low stock items</h5>
                          <p class="empty-subtitle">All inventory items are at adequate stock levels.</p>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="panel-footer">
              <div>
                <select class="form-select" [(ngModel)]="lowStockPageSize" (change)="onLowStockPageSizeChange()" style="width: auto; min-width: 160px;">
                  <option [value]="10">10 items per page</option>
                  <option [value]="25">25 items per page</option>
                  <option [value]="50">50 items per page</option>
                </select>
              </div>
              <ngb-pagination
                [collectionSize]="lowStockTotalItems"
                [(page)]="lowStockCurrentPage"
                [pageSize]="lowStockPageSize"
                (pageChange)="onLowStockPageChange($event)"
                [maxSize]="5"
                [rotate]="true"
                [boundaryLinks]="true">
              </ngb-pagination>
            </div>
          </ng-template>
        </li>

        <li [ngbNavItem]="3">
          <button ngbNavLink>
            <i class="fas fa-calendar-times me-2"></i>Expiration Date Report
          </button>
          <ng-template ngbNavContent>
            <!-- Expiration Date Filters -->
            <div class="filters-panel" style="border-radius: 0; border-left: 0; border-right: 0; box-shadow: none; margin-bottom: 0;">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div class="search-box" style="flex: 1; max-width: 320px;">
                  <div class="search-input-wrapper">
                    <i class="fas fa-search search-icon"></i>
                    <input class="search-input" type="text" placeholder="Search" />
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <div ngbDropdown class="d-inline-block" container="body">
                    <button type="button" class="btn btn-filter" id="dropdownBasic1" ngbDropdownToggle>
                      <i class="fas fa-calendar me-2"></i>Filter by Date
                      <i class="fas fa-chevron-down ms-2"></i>
                    </button>
                    <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="p-1">
                      <button ngbDropdownItem>Today</button>
                      <button ngbDropdownItem>This week</button>
                      <button ngbDropdownItem>This Month</button>
                      <button ngbDropdownItem>Custom</button>
                    </div>
                  </div>
                  <div ngbDropdown class="d-inline-block" container="body">
                    <button type="button" class="btn btn-filter" id="dropdownBasic1" ngbDropdownToggle>
                      <i class="fas fa-filter me-2"></i>Filter by Category
                      <i class="fas fa-chevron-down ms-2"></i>
                    </button>
                    <div ngbDropdownMenu aria-labelledby="dropdownBasic1" class="p-1">
                      <button ngbDropdownItem>Today</button>
                      <button ngbDropdownItem>This week</button>
                      <button ngbDropdownItem>This Month</button>
                      <button ngbDropdownItem>Custom</button>
                    </div>
                  </div>
                  <button class="btn btn-filter">
                    <i class="fas fa-file-export me-2"></i>Export Report
                  </button>
                </div>
              </div>
            </div>

            <!-- Expiration Date Table -->
            <div class="table-container">
              <table class="table">
                <thead>
                  <tr>
                    <th class="ps-4">Item Name</th>
                    <th>Expiration Date</th>
                    <th>Current Quantity</th>
                    <th>Days Past Expiration</th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of expirationReport; track $index) {
                  <tr>
                    <td class="ps-4 fw-medium">{{ item.inventory_name }}</td>
                    <td>
                      <span class="badge badge-soft-warning">
                        {{ item?.expiration_date | date : 'DD - MMM - YYYY' }}
                      </span>
                    </td>
                    <td>{{ item?.current_quantity }}</td>
                    <td>
                      <span class="badge badge-soft-danger">
                        {{ item.days_past_expiration }} days
                      </span>
                    </td>
                  </tr>
                  }
                  @empty {
                    <tr>
                      <td colspan="4">
                        <div class="empty-state">
                          <div class="empty-icon">
                            <i class="fas fa-calendar-check fa-3x"></i>
                          </div>
                          <h5 class="empty-title">No expiration data</h5>
                          <p class="empty-subtitle">No items with expiration dates found.</p>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </ng-template>
        </li>
      </ul>
    </div>

    <div [ngbNavOutlet]="nav"></div>
  </div>
</div>
